import { LitElement } from "lit";
import { customElement, property } from "lit/decorators.js";
import { effect } from "@preact/signals-core";
import {
  registerCard,
  changeCardVariant,
  getCardSignals,
  CardConfig,
} from "../store/collection-store";
import { buildVariantUrl } from "../utils/knocking";

/**
 * Invisible bootstrap element for each product/variant card.
 * Must be the first child of <product-card> in the liquid template.
 *
 * Responsibilities:
 *  - Reads product JSON + inventory quantities from embedded <script> tags
 *  - Determines the initial variant (first available, or pinned for variant-cards)
 *  - Registers the card's signal state in the collection store
 *  - Watches the hidden variant input for changes (from variant-dropdown-cp or
 *    any other vanilla selector) and forwards them to the store
 */
@customElement("card-provider")
export class CardProvider extends LitElement {
  @property({ type: String, attribute: "data-card-id" }) cardId = "";
  @property({ type: Number, attribute: "data-initial-variant-id" }) initialVariantId = 0;
  @property({ type: Boolean, attribute: "data-is-variant-card" }) isVariantCard = false;
  @property({ type: Number, attribute: "data-option-size" }) optionSize = 1;
  @property({ type: Boolean, attribute: "data-extended-offer-enabled" }) extendedOfferEnabled = false;
  @property({ type: Boolean, attribute: "data-has-custom-text" }) hasCustomText = false;
  @property({ type: Boolean, attribute: "data-customizable" }) customizable = false;
  @property({ type: Boolean, attribute: "data-special-offer-enabled" }) specialOfferEnabled = false;

  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this.cardId) return;

    const card = this.closest("product-card");
    if (!card) return;

    // ── 1. Parse product JSON ──────────────────────────────────────────────
    const productEl = card.querySelector(".product-card-product");
    if (!productEl?.textContent) {
      console.error(`card-provider [${this.cardId}]: product JSON not found`);
      return;
    }
    const productData = JSON.parse(productEl.textContent);

    // ── 2. Inject inventory quantities into variants ───────────────────────
    const qtyEl = card.querySelector("#variant-quantities");
    if (qtyEl?.textContent) {
      if (this.isVariantCard) {
        // variant-card embeds a single quantity for the pinned variant
        const qty = parseInt(qtyEl.textContent.trim(), 10);
        const pinned = productData.variants.find(
          (v: any) => v.id === this.initialVariantId
        );
        if (pinned) pinned.inventoryQuantity = isNaN(qty) ? 0 : qty;
      } else {
        // product-card embeds "/"-delimited quantities in variant order
        const qtys = qtyEl.textContent
          .split("/")
          .filter(Boolean)
          .map((s: string) => parseInt(s.trim(), 10));
        productData.variants.forEach((v: any, i: number) => {
          v.inventoryQuantity = isNaN(qtys[i]) ? 0 : qtys[i];
        });
      }
    }

    // ── 3. Read hidden variant IDs ─────────────────────────────────────────
    const hiddenEl = card.querySelector("#hidden-variant-ids");
    const hiddenVariantIds = new Set<number>();
    if (hiddenEl?.textContent) {
      try {
        const arr = JSON.parse(hiddenEl.textContent);
        if (Array.isArray(arr)) {
          arr.forEach((id: number) => hiddenVariantIds.add(id));
        }
      } catch {
        // ignore malformed JSON
      }
    }

    // ── 4. Determine initial variant ──────────────────────────────────────
    const allVariants: any[] = productData.variants ?? [];
    const visible = allVariants.filter((v) => !hiddenVariantIds.has(v.id));

    let initialId: number | null = null;
    if (this.isVariantCard) {
      initialId = this.initialVariantId || null;
    } else {
      const first =
        visible.find((v) => (v.inventoryQuantity ?? 0) > 0) ??
        visible.find((v) => v.available) ??
        visible[0] ??
        null;
      initialId = first?.id ?? null;
    }

    // ── 5. Build config and register ──────────────────────────────────────
    const isCbsBox = (productData.tags ?? []).includes("cbs-box");
    const config: CardConfig = {
      optionSize: this.optionSize,
      visibleVariantCount: this.isVariantCard ? 1 : visible.length,
      hasCustomText: this.hasCustomText,
      isCustomizable: this.customizable,
      extendedOfferEnabled: this.extendedOfferEnabled,
      specialOfferEnabled: this.specialOfferEnabled,
      isCbsBox,
      hiddenVariantIds,
    };

    registerCard(this.cardId, productData, config, initialId);

    // ── 6. Sync <a data-variant-href-base> links with selected variant ─────
    effect(() => {
      const variantId = getCardSignals(this.cardId)?.selectedVariantId.value;
      const links = document.querySelectorAll<HTMLAnchorElement>(
        `[data-variant-href-base][data-card-id="${this.cardId}"]`
      );
      links.forEach((link) => {
        const base = link.dataset.variantHrefBase ?? link.href;
        link.href = buildVariantUrl(base, variantId);
      });
    });

    // ── 7. Watch the hidden variant input for changes ──────────────────────
    // variant-dropdown-cp (and other selectors) update input[name="id"].
    // We mirror those changes into the collection store signal.
    const variantInput = card.querySelector<HTMLInputElement>('input[name="id"]');
    if (variantInput) {
      variantInput.addEventListener("change", (e) => {
        const id = parseInt((e.target as HTMLInputElement).value, 10);
        if (!isNaN(id)) changeCardVariant(this.cardId, id);
      });
    }
  }

  render() {
    return null;
  }
}
